import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faHouseLaptop, faNotesMedical } from '@fortawesome/pro-light-svg-icons';
import { Spinner, SpinnerSize } from '@fluentui/react';
import EmployeeLeaveService from '../../../../services/employees/leave';
import { IEmployeeLeaveBalanceProps } from '../../../../props/employees/leaves/leave';
import styles from './styles.module.scss';

export interface EmployeeLeaveBalanceCardsProps {
    employeeId: string;
    year?: number | string;
    refreshTrigger?: number;
    onBalanceLoaded?: (balance: IEmployeeLeaveBalanceProps) => void;
}

const EmployeeLeaveBalanceCards: React.FC<EmployeeLeaveBalanceCardsProps> = (props: EmployeeLeaveBalanceCardsProps) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [balance, setBalance] = useState<IEmployeeLeaveBalanceProps | undefined>();

    const effectiveYear = props.year && props.year !== 'all' ? Number(props.year) : moment().year();

    useEffect(() => {
        loadBalance();
    }, [props.employeeId, props.year, props.refreshTrigger]);

    const loadBalance = async () => {
        if (!props.employeeId) return;
        try {
            setLoading(true);
            const bal = await EmployeeLeaveService.getBalance(`employeeId=${props.employeeId}&year=${effectiveYear}`);
            setBalance(bal);
            props.onBalanceLoaded?.(bal);
        } catch (e) {
            console.error("Failed to load leave balance:", e);
        } finally {
            setLoading(false);
        }
    };

    if (loading && !balance) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner size={SpinnerSize.small} label={`Loading leave balance for ${effectiveYear}...`} labelPosition="right" />
            </div>
        );
    }

    if (!balance) return null;

    const annualQuota = balance.annualQuota || 12;
    const annualRemaining = Math.max(0, balance.annualRemaining);
    const annualPercent = annualQuota > 0 ? Math.min(100, Math.round((annualRemaining / annualQuota) * 100)) : 0;

    const wfhQuota = balance.wfhQuota || 3;
    const wfhRemaining = Math.max(0, balance.wfhRemaining);
    const wfhPercent = wfhQuota > 0 ? Math.min(100, Math.round((wfhRemaining / wfhQuota) * 100)) : 0;

    return (
        <div className={styles.balanceGrid}>
            {/* Card 1: Annual Leave */}
            <div className={styles.balanceCard}>
                <div className={styles.cardHeader}>
                    <div>
                        <div className={styles.cardTitle}>Annual Leave</div>
                        <div className={styles.yearBadge}>{effectiveYear} Period</div>
                    </div>
                    <div className={`${styles.iconWrapper} ${styles.annualIcon}`}>
                        <FontAwesomeIcon icon={faCalendarCheck} />
                    </div>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.mainValue}>
                        <span className={styles.number}>{balance.annualRemaining}</span>
                        <span className={styles.unit}>/ {balance.annualQuota} days left</span>
                    </div>
                    <div className={styles.progressBarBg}>
                        <div className={styles.progressBarFillAnnual} style={{ width: `${annualPercent}%` }} />
                    </div>
                    <div className={styles.subInfo}>
                        <span>Used: <strong>{balance.annualUsed} days</strong></span>
                        <span className={styles.dot}>•</span>
                        <span>Quota: <strong>{balance.annualQuota} days</strong></span>
                    </div>
                </div>
            </div>

            {/* Card 2: WFH Quota */}
            <div className={styles.balanceCard}>
                <div className={styles.cardHeader}>
                    <div>
                        <div className={styles.cardTitle}>WFH Allowance</div>
                        <div className={styles.yearBadge}>{effectiveYear} Period</div>
                    </div>
                    <div className={`${styles.iconWrapper} ${styles.wfhIcon}`}>
                        <FontAwesomeIcon icon={faHouseLaptop} />
                    </div>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.mainValue}>
                        <span className={styles.number}>{balance.wfhRemaining}</span>
                        <span className={styles.unit}>/ {balance.wfhQuota} days left</span>
                    </div>
                    <div className={styles.progressBarBg}>
                        <div className={styles.progressBarFillWfh} style={{ width: `${wfhPercent}%` }} />
                    </div>
                    <div className={styles.subInfo}>
                        <span>Used: <strong>{balance.wfhUsed} days</strong></span>
                        <span className={styles.dot}>•</span>
                        <span>Quota: <strong>{balance.wfhQuota} days</strong></span>
                    </div>
                </div>
            </div>

            {/* Card 3: Special & Sick Leave */}
            <div className={styles.balanceCard}>
                <div className={styles.cardHeader}>
                    <div>
                        <div className={styles.cardTitle}>Special & Sick Leave</div>
                        <div className={styles.yearBadge}>{effectiveYear} Period</div>
                    </div>
                    <div className={`${styles.iconWrapper} ${styles.specialIcon}`}>
                        <FontAwesomeIcon icon={faNotesMedical} />
                    </div>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.mainValue}>
                        <span className={styles.number}>{balance.specialUsed}</span>
                        <span className={styles.unit}>days used</span>
                    </div>
                    <div className={styles.progressBarBg}>
                        <div className={styles.progressBarFillSpecial} style={{ width: '100%' }} />
                    </div>
                    <div className={styles.subInfo}>
                        <span>Sick, maternity, bereavement (exempt from quota)</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeLeaveBalanceCards;
